import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

interface Props {
  waarde: string;
  onChange: (waarde: string) => void;
  placeholder?: string | undefined;
  autoFocus?: boolean | undefined;
}

/**
 * Een zoekveld dat de zoekterm pas doorgeeft als de gebruiker even stopt met typen.
 * @param props De huidige zoekterm, wat er gebeurt bij een nieuwe term en de placeholder.
 * @returns Het veld.
 */
export function Zoekveld({ waarde, onChange, placeholder = 'Zoeken…', autoFocus }: Props) {
  const [tekst, setTekst] = useState(waarde);
  const gemeld = useRef(waarde);
  const melder = useRef(onChange);
  melder.current = onChange;

  useEffect(() => {
    if (waarde !== gemeld.current) {
      gemeld.current = waarde;
      setTekst(waarde);
    }
  }, [waarde]);

  useEffect(() => {
    if (tekst === gemeld.current) {
      return undefined;
    }
    const timer = setTimeout(() => {
      gemeld.current = tekst;
      melder.current(tekst);
    }, 300);
    return () => clearTimeout(timer);
  }, [tekst]);

  return (
    <TextField
      value={tekst}
      onChange={(gebeurtenis) => setTekst(gebeurtenis.target.value)}
      placeholder={placeholder}
      size="small"
      autoFocus={autoFocus ?? false}
      slotProps={{
        htmlInput: { 'aria-label': placeholder },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: tekst ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Zoekterm wissen"
                onClick={() => {
                  setTekst('');
                  gemeld.current = '';
                  melder.current('');
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      }}
      sx={{ minWidth: { sm: 280 } }}
    />
  );
}
